import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { getCurrentSemanticMapId } from "Slices/appState";
import { getVehicleTypes } from "Slices/vehicleTypes";
import { getVehicles } from "Slices/vehicles";
import { getMissionsByVehicleId } from "Slices/vehiclesPage";
import {
  fetchGetIntersections,
  getIntersectionEntries,
  getIsIntersectionsLoaded,
  getIsIntersectionsLoadingError,
  getSematicIntersectionMapping,
} from "Slices/intersections";
import { AsideLoader } from "Components/common/Loader/index";

import { IntersectionItem } from "./IntersectionItem";
import {
  IntersectionListBodyStyled,
  IntersectionItemWrapperStyled,
} from "./styles";
import { buildIntersectionNameAndPermissionsMap } from "../utils";

export const IntersectionEntities = memo(() => {
  const currentSemanticMapId = useSelector(getCurrentSemanticMapId);
  const semanticIntersectionMapping = useSelector(
    getSematicIntersectionMapping
  );
  const intersectionData = useSelector(getIntersectionEntries);
  const isIntersectionsLoaded = useSelector(getIsIntersectionsLoaded);
  const hasIntersectionsLoadingError = useSelector(
    getIsIntersectionsLoadingError
  );
  const vehicleTypes: VehicleType[] = useSelector(getVehicleTypes);
  const vehicles: Array<Vehicle> = useSelector(getVehicles);
  const missionsByVehicleId = useSelector(getMissionsByVehicleId);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number>(Date.now());

  const dispatch = useDispatch();
  const handleRefresh = useCallback(() => {
    if (!semanticIntersectionMapping || !currentSemanticMapId) {
      return;
    }

    const mappings = semanticIntersectionMapping[currentSemanticMapId];
    const intersectionIds = mappings
      ? Object.keys(mappings).map((each) => parseInt(each))
      : [];

    dispatch(fetchGetIntersections(intersectionIds));
    setLastUpdatedAt(Date.now());
  }, [dispatch, semanticIntersectionMapping, currentSemanticMapId]);

  useEffect(() => {
    if (isIntersectionsLoaded && !hasIntersectionsLoadingError) {
      setLastUpdatedAt(Date.now());
    }
  }, [isIntersectionsLoaded, hasIntersectionsLoadingError]);

  const details = useMemo(() => {
    if (
      currentSemanticMapId &&
      semanticIntersectionMapping?.[currentSemanticMapId]
    ) {
      return buildIntersectionNameAndPermissionsMap(
        semanticIntersectionMapping[currentSemanticMapId],
        intersectionData,
        vehicles,
        vehicleTypes,
        missionsByVehicleId
      );
    }
    return [];
  }, [
    intersectionData,
    currentSemanticMapId,
    semanticIntersectionMapping,
    vehicles,
    vehicleTypes,
    missionsByVehicleId,
  ]);

  return (
    <IntersectionListBodyStyled data-testid="intersection-list-entities">
      {hasIntersectionsLoadingError ? (
        <IntersectionItemWrapperStyled standAlone={true}>
          Something went wrong. Please try it again.
        </IntersectionItemWrapperStyled>
      ) : !isIntersectionsLoaded ? (
        <AsideLoader />
      ) : currentSemanticMapId &&
        semanticIntersectionMapping &&
        semanticIntersectionMapping[currentSemanticMapId] === undefined ? (
        <IntersectionItemWrapperStyled standAlone={true}>
          No intersections configured
        </IntersectionItemWrapperStyled>
      ) : (
        details.map((intersection) => {
          const { id } = intersection;
          return (
            <IntersectionItem
              onRefresh={handleRefresh}
              data={intersection}
              key={id}
              lastUpdatedAt={lastUpdatedAt}
            />
          );
        })
      )}
    </IntersectionListBodyStyled>
  );
});
